import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as servicesMeetingList from 'services/meetingNotification';

// ✅ Query key factory
const meetingKeys = {
  all: ['meetings'],
  lists: () => [...meetingKeys.all, 'list'],
  list: (filters) => [...meetingKeys.lists(), filters],
  notifications: () => [...meetingKeys.all, 'notifications'],
};

// ✅ Default parameters
const defaultMeetingParams = {
  pageIndex: 1,
  pageSize: 9999,
  filters: [
    {
      fieldName: 'meetingStatus',
      filterType: 'NotEqual',
      value: 'Closed',
    },
  ],
};

// ✅ Main hook for meeting notifications
export const useMeetingNotifications = (options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: meetingKeys.notifications(),
    queryFn: async () => {
      const response = await servicesMeetingList.getMeetingNotification(
        defaultMeetingParams
      );

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch meetings');
      }

      return response.data?.gridData || [];
    },
    enabled,
    refetchInterval: 5 * 60 * 1000, // 5 minutes (replaces your setInterval)
    ...queryOptions,
  });

  // ✅ Computed values
  const processedData = useMemo(() => {
    if (!query.data) {
      return {
        meetingList: [],
        pendingMeetings: [],
        pendingCount: 0,
        totalMeetingList: 0,
      };
    }

    const meetingList = query.data;
    const pendingMeetings = meetingList.filter(
      (item) => item.meetingUserStatus === 'Pending'
    );

    return {
      meetingList,
      pendingMeetings,
      pendingCount: pendingMeetings.length,
      totalMeetingList: meetingList.length,
    };
  }, [query.data]);

  const invalidateMeetingList = () => {
    queryClient.invalidateQueries({
      queryKey: meetingKeys.notifications(),
    });
  };

  return {
    ...processedData,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
    invalidateMeetingList,
  };
};
